'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import UserAvatarSkeleton from '~/shared/components/header/components/user-avatar/components/user-avatar-skeleton';
import Link from '~/shared/components/link';
import { PAGE_NAMES } from '~/shared/constants';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import type { ShortUserType } from '~/shared/types/user-type';
import type { Locale } from '~/universal/constants';

import useMenuBar from './use-menu-bar';

import './menu-bar.scss';

import { checkIsClientsPanel } from '~/shared/helpers/check-helpers';

const NotificationsBadge = dynamic(() => import('~/shared/components/header/components/notifications-badge'), {
  ssr: false,
});

const UserAvatar = dynamic(() => import('~/shared/components/header/components/user-avatar/user-avatar'), {
  ssr: false,
  loading: () => <UserAvatarSkeleton />,
});

export const MenuList = ({ menuItems, closeMenu, onMouseEnter, onMouseLeave }) => {
  const pathname = usePathname();

  const isItemActive = (item: { key: string; activePaths?: string[]; excludePaths?: string[] }) => {
    if (item.excludePaths?.some((path) => pathname.startsWith(path))) {
      return false;
    }

    if (item.activePaths?.length) {
      return item.activePaths.some((path) => pathname.startsWith(path));
    }

    return item.key === pathname;
  };

  return (
    <div className="menu-bar__dropdown" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div className="menu-bar__dropdown-inner">
        {menuItems.map((item) => {
          if (item.href) {
            return (
              <Link
                key={item.key}
                href={item.href}
                className={classNames('menu-bar__sub-item', {
                  'menu-bar__sub-item--selected': isItemActive(item),
                })}
                onClick={closeMenu}
              >
                {item.label}
              </Link>
            );
          }

          return (
            <button
              key={item.key}
              type="button"
              className={classNames('menu-bar__sub-item', {
                'menu-bar__sub-item--selected': isItemActive(item),
              })}
              onClick={() => {
                closeMenu();
                item.onClick?.();
              }}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

type MenuBarProps = Readonly<{
  lng: Locale;
  isNoPopup?: boolean;
  user: ShortUserType | null;
  onSignOutCallback?: () => void;
}>;

const MenuBar = ({ lng, onSignOutCallback, user, isNoPopup = false }: MenuBarProps) => {
  const { isAuthenticated, menuItems, containerRef, pathname, isOpen, scheduleCloseMenu, closeMenu, openMenu } =
    useMenuBar({
      lng,
      user,
      onSignOutCallback,
    });
  const isClientsPanel = checkIsClientsPanel(pathname);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <div
      ref={containerRef}
      className={classNames('menu-bar', {
        'menu-bar--open': isOpen,
      })}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleCloseMenu}
    >
      <div className="menu-bar__avatar-wrap">
        <button
          type="button"
          className="menu-bar__trigger"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label="Open user menu"
          onMouseEnter={openMenu}
          onFocus={openMenu}
          onClick={openMenu}
        >
          <UserAvatar user={user} isAuthenticated={isAuthenticated} />
        </button>

        {!isNoPopup && isMounted && isAuthenticated ? (
          <Link
            href={getLocaleLink(
              isClientsPanel ? PAGE_NAMES.CLIENTS_NOTIFICATIONS : PAGE_NAMES.PLATFORM_USER_NOTIFICATIONS,
              lng,
            )}
            className="menu-bar__notifications-link"
            onClick={(event) => {
              event.stopPropagation();
              closeMenu();
            }}
          >
            <NotificationsBadge isAuthenticated={isAuthenticated} />
          </Link>
        ) : null}

        {!isNoPopup && isOpen ? (
          <MenuList
            menuItems={menuItems}
            closeMenu={closeMenu}
            onMouseEnter={openMenu}
            onMouseLeave={scheduleCloseMenu}
          />
        ) : null}
      </div>
    </div>
  );
};

export default MenuBar;

